import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { CHAIN_LABELS, STRATEGY_LABELS, formatTime } from '../utils/masterChefUtils';

function fmtUsd(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return '-';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(2)}`;
}

const DECISION_BADGE = {
  entered: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  skipped: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
  buy_failed: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  queued: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
};
const DECISION_LABEL = { entered: 'Entered', skipped: 'Skipped', buy_failed: 'Buy Failed', queued: 'Queued' };

export default function EntryLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [decision, setDecision] = useState('');
  const [chain, setChain] = useState('');
  const [type, setType] = useState('');
  const [strategyId, setStrategyId] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('recent');
  const [missedOnly, setMissedOnly] = useState(false);
  const [page, setPage] = useState(0);
  const limit = 100;

  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    if (decision) p.set('decision', decision);
    if (chain) p.set('chain', chain);
    if (type) p.set('type', type);
    if (strategyId) p.set('strategyId', strategyId);
    if (search) p.set('search', search);
    if (sort) p.set('sort', sort);
    if (missedOnly) p.set('missedOnly', 'true');
    return p;
  }, [decision, chain, type, strategyId, search, sort, missedOnly]);

  const fetchLogs = useCallback(async () => {
    try {
      const params = buildParams();
      params.set('limit', limit);
      params.set('skip', page * limit);
      const [logRes, statRes] = await Promise.all([
        api.get(`/entry-logs?${params}`),
        api.get(`/entry-logs/stats?${buildParams()}`),
      ]);
      setLogs(logRes.data.logs || []);
      setTotal(logRes.data.total || 0);
      setStats(statRes.data);
    } catch (err) {
      console.error('Failed to fetch entry logs:', err);
    } finally {
      setLoading(false);
    }
  }, [buildParams, page]);

  useEffect(() => {
    api.get('/entry-logs/strategies').then((r) => setStrategies(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchLogs();
    const interval = setInterval(fetchLogs, 20000);
    return () => clearInterval(interval);
  }, [fetchLogs]);

  useEffect(() => { setPage(0); }, [decision, chain, type, strategyId, search, sort, missedOnly]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/entry-logs/export.csv?${buildParams()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `chef-entry-logs-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.ceil(total / limit);
  const d = stats?.decisions || {};

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Chef Entry Logs</h2>
          <p className="text-xs text-gray-500">
            Every candidate a user strategy evaluated — entered, skipped (with reason), failed or queued.
            Use it to tune entries without missing trades.
          </p>
        </div>
        <button onClick={exportCsv} disabled={exporting}
          className="text-xs px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 self-start">
          {exporting ? 'Exporting…' : '⬇ Export CSV'}
        </button>
      </div>

      {/* Stat cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {[
            { k: 'entered', label: 'Entered', cls: 'text-green-600 dark:text-green-400' },
            { k: 'skipped', label: 'Skipped', cls: 'text-gray-600 dark:text-gray-300' },
            { k: 'buy_failed', label: 'Buy Failed', cls: 'text-red-600 dark:text-red-400' },
            { k: 'queued', label: 'Queued', cls: 'text-blue-600 dark:text-blue-400' },
          ].map((c) => (
            <div key={c.k} className="bg-white dark:bg-gray-800 rounded-lg shadow p-3">
              <div className="text-[10px] uppercase tracking-wide text-gray-400">{c.label}</div>
              <div className={`text-xl font-bold ${c.cls}`}>{d[c.k] || 0}</div>
            </div>
          ))}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3">
            <div className="text-[10px] uppercase tracking-wide text-gray-400">Missed (≥2x)</div>
            <div className="text-xl font-bold text-amber-600 dark:text-amber-400">{stats.missed?.count || 0}</div>
            <div className="text-[10px] text-gray-400">
              unique tokens · soft skips only · max {stats.missed?.maxMultiple ? `${stats.missed.maxMultiple.toFixed(1)}x` : '-'}
            </div>
          </div>
        </div>
      )}

      {/* Top skip reasons */}
      {stats?.topSkipReasons?.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3">
          <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2">Top skip reasons</div>
          <div className="flex flex-wrap gap-1.5">
            {stats.topSkipReasons.map((r, i) => (
              <span key={i} className="text-[11px] px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                {r.reason} <span className="font-semibold text-gray-900 dark:text-white">×{r.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Token / address…"
          className="text-xs px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
        <select value={strategyId} onChange={(e) => setStrategyId(e.target.value)}
          className="text-xs px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
          <option value="">All strategies</option>
          {strategies.map((s) => (
            <option key={s.strategyId} value={s.strategyId}>{s.name || s.strategyId} ({s.count})</option>
          ))}
        </select>
        <select value={decision} onChange={(e) => setDecision(e.target.value)}
          className="text-xs px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
          <option value="">All decisions</option>
          <option value="entered">Entered</option>
          <option value="skipped">Skipped</option>
          <option value="buy_failed">Buy Failed</option>
          <option value="queued">Queued</option>
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)}
          className="text-xs px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
          <option value="">All types</option>
          <option value="recovery">Recovery</option>
          <option value="pump">Pump</option>
          <option value="volume">Volume</option>
          <option value="ema">EMA</option>
        </select>
        <select value={chain} onChange={(e) => setChain(e.target.value)}
          className="text-xs px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
          <option value="">All chains</option>
          <option value="solana">Solana</option>
          <option value="base">Base</option>
          <option value="ethereum">Ethereum</option>
          <option value="bsc">BSC</option>
          <option value="robinhood">Robinhood</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)}
          className="text-xs px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
          <option value="recent">Most recent</option>
          <option value="missed">Biggest miss</option>
          <option value="peak">Peak MC</option>
          <option value="skips">Most skips</option>
          <option value="seen">Most evaluated</option>
        </select>
        <label className="text-xs flex items-center gap-1.5 px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer">
          <input type="checkbox" checked={missedOnly} onChange={(e) => setMissedOnly(e.target.checked)} />
          Missed only (≥2x)
        </label>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {loading && logs.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400">Loading entry logs…</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400">No entry decisions match filters yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] text-gray-500 dark:text-gray-400 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <th className="text-left px-3 py-2">Last seen</th>
                  <th className="text-left px-2 py-2">Token</th>
                  <th className="text-left px-2 py-2">Chain</th>
                  <th className="text-left px-2 py-2">Strategy</th>
                  <th className="text-center px-2 py-2">Decision</th>
                  <th className="text-left px-2 py-2">Reason / comment</th>
                  <th className="text-right px-2 py-2">MC @ scan</th>
                  <th className="text-right px-2 py-2">Peak MC</th>
                  <th className="text-right px-2 py-2">Missed</th>
                  <th className="text-right px-2 py-2">Rec%</th>
                  <th className="text-right px-2 py-2">Skips</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => {
                  const missed = l.missedMultiple;
                  const missedHot = l.decision !== 'entered' && missed >= 2;
                  return (
                    <tr key={l._id} className="border-b dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{formatTime(l.lastSeenAt)}</td>
                      <td className="px-2 py-2">
                        <div className="font-medium text-gray-900 dark:text-white">{l.tokenSymbol || '?'}</div>
                        <div className="text-[10px] text-gray-400 font-mono">{(l.tokenAddress || '').slice(0, 10)}…</div>
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-600 dark:text-gray-400">{CHAIN_LABELS[l.chain] || l.chain}</td>
                      <td className="px-2 py-2 text-xs text-gray-600 dark:text-gray-400">
                        {l.strategyName || '-'}
                        <div className="text-[10px] text-gray-400">{STRATEGY_LABELS[l.strategyType] || l.strategyType}</div>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded ${DECISION_BADGE[l.decision] || ''}`}>
                          {DECISION_LABEL[l.decision] || l.decision}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-600 dark:text-gray-300 max-w-[280px] truncate" title={l.reason}>
                        {l.reason || '-'}
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-xs text-gray-500">{fmtUsd(l.firstMarketCap)}</td>
                      <td className="px-2 py-2 text-right font-mono text-xs text-gray-500">{fmtUsd(l.peakMarketCap)}</td>
                      <td className={`px-2 py-2 text-right font-mono text-xs ${missedHot ? 'text-amber-600 dark:text-amber-400 font-semibold' : 'text-gray-400'}`}>
                        {missed ? `${missed.toFixed(1)}x` : '-'}
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-xs text-gray-500">
                        {l.metrics?.recoveryPercent != null ? `${l.metrics.recoveryPercent.toFixed(0)}%` : '-'}
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-xs text-gray-500">{l.skipCount || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 text-xs rounded bg-gray-100 dark:bg-gray-700 disabled:opacity-40">Previous</button>
            <span className="text-xs text-gray-500">Page {page + 1} of {totalPages} ({total} tokens)</span>
            <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 text-xs rounded bg-gray-100 dark:bg-gray-700 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>
    </div>
  );
}
